/**
 * QMS Phase 1: Requirement-level compliance and gap report.
 * Shows compliance % by standard/domain/policy and list of requirements not Implemented/Verified.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequirementCompliance, useGapReport } from '../../hooks/useQmsCompliance';
import './ComplianceReportView.css';

const ComplianceReportView = ({ organizationId }) => {
  const { t } = useTranslation('qms');
  const [domainFilter, setDomainFilter] = useState('');

  const options = domainFilter ? { domain: domainFilter } : {};
  const { compliance, loading: complianceLoading, error: complianceError, refresh: refreshCompliance } = useRequirementCompliance(organizationId, options);
  const { gaps, loading: gapsLoading, error: gapsError, refresh: refreshGaps } = useGapReport(organizationId, { ...options, limit: 100 });

  const handleRefresh = () => {
    refreshCompliance();
    refreshGaps();
  };

  if (!organizationId) {
    return (
      <div className="compliance-report-view">
        <p className="compliance-report-empty">{t('selectOrganization')}</p>
      </div>
    );
  }

  const loading = complianceLoading || gapsLoading;
  const error = complianceError || gapsError;

  return (
    <div className="compliance-report-view">
      <div className="compliance-report-header">
        <h2>{t('complianceReport')}</h2>
        <div className="compliance-report-actions">
          <select
            className="filter-select"
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            aria-label={t('filterByDomain')}
          >
            <option value="">{t('allDomains')}</option>
            <option value="IPC">{t('nomenclatures:options.domainIPC')}</option>
            <option value="Clinical Quality">{t('nomenclatures:options.domainClinicalQuality')}</option>
            <option value="Patient Safety">{t('nomenclatures:options.domainPatientSafety')}</option>
            <option value="Operational">{t('nomenclatures:options.domainOperational')}</option>
            <option value="Organizational">{t('nomenclatures:options.domainOrganizational')}</option>
          </select>
          <button type="button" className="btn-refresh" onClick={handleRefresh} disabled={loading}>
            {t('refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="compliance-report-error">
          {error}
        </div>
      )}

      {loading && !compliance && (
        <div className="compliance-report-loading">{t('loading')}</div>
      )}

      {!loading && compliance && (
        <>
          <section className="compliance-summary-section">
            <h3>{t('complianceSummary')}</h3>
            <div className="compliance-summary-cards">
              <div className="compliance-card">
                <span className="compliance-card-label">{t('overallCompliance')}</span>
                <span className="compliance-card-value">{compliance.overall}%</span>
                <span className="compliance-card-detail">
                  {compliance.compliant} / {compliance.total} {t('requirementsMet')}
                </span>
              </div>
              {compliance.criticalOnly != null && (
                <div className="compliance-card compliance-card-critical">
                  <span className="compliance-card-label">{t('criticalCompliance')}</span>
                  <span className="compliance-card-value">{compliance.criticalOnly}%</span>
                  <span className="compliance-card-detail">
                    {compliance.criticalCompliant} / {compliance.criticalTotal} MUST {t('requirementsMet')}
                  </span>
                </div>
              )}
            </div>
            {compliance.total === 0 && (
              <p className="compliance-report-empty">{t('noRequirementsLinked')}</p>
            )}
          </section>

          {Object.keys(compliance.byDomain).length > 0 && (
            <section className="compliance-breakdown-section">
              <h3>{t('complianceByDomain')}</h3>
              <div className="compliance-breakdown-list">
                {Object.entries(compliance.byDomain).map(([domain, val]) => {
                  const isObj = typeof val === 'object' && val !== null;
                  const pct = isObj ? (val.percent ?? 0) : (typeof val === 'number' ? val : 0);
                  const adoptionPct = isObj ? (val.adoptionPercent ?? pct) : pct;
                  const label = isObj ? (val.name || domain) : domain;
                  const total = isObj ? val.total : null;
                  const comp = isObj ? val.compliant : null;
                  const prog = isObj ? (val.inProgress ?? 0) : 0;
                  return (
                    <div key={domain} className="breakdown-row">
                      <span className="breakdown-label">{label}</span>
                      <div className="breakdown-bar-wrap">
                        <div className="breakdown-bar-track">
                          <div className="breakdown-bar-fill fill-good" style={{ width: `${Math.min(pct, 100)}%` }} />
                          {(adoptionPct - pct) > 0 && (
                            <div className="breakdown-bar-fill fill-progress" style={{ width: `${Math.min(adoptionPct - pct, 100 - pct)}%` }} />
                          )}
                        </div>
                        <span className="breakdown-value">
                          {pct}%
                          {prog > 0 && <span className="breakdown-inprogress"> (+{prog} în lucru)</span>}
                        </span>
                      </div>
                      {total !== null && (
                        <span className="breakdown-detail">{comp}/{total} cerințe</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      <section className="gap-report-section">
        <h3>{t('gapReport')}</h3>
        <p className="gap-report-desc">{t('gapReportDescription')}</p>
        {gapsLoading && <div className="compliance-report-loading">{t('loading')}</div>}
        {!gapsLoading && gaps.length === 0 && compliance?.total > 0 && (
          <p className="compliance-report-success">{t('noGaps')}</p>
        )}
        {!gapsLoading && gaps.length === 0 && compliance?.total === 0 && (
          <p className="compliance-report-empty">{t('noRequirementsLinked')}</p>
        )}
        {!gapsLoading && gaps.length > 0 && (
          <div className="gap-report-table-wrap">
            <table className="gap-report-table">
              <thead>
                <tr>
                  <th>{t('complianceLevel')}</th>
                  <th>{t('code')}</th>
                  <th>{t('requirementText')}</th>
                  <th>{t('standard')}</th>
                  <th>{t('policySource')}</th>
                  <th>{t('implementationStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {gaps.map((g) => (
                  <tr key={g.standardRequirementId}>
                    <td>
                      <span className={`priority-badge priority-${g.complianceLevel?.toLowerCase() || 'may'}`}>
                        {g.complianceLevel}
                      </span>
                    </td>
                    <td>{g.requirement?.code || '—'}</td>
                    <td className="requirement-text">{g.requirement?.requirementText?.slice(0, 80) || '—'}{g.requirement?.requirementText?.length > 80 ? '…' : ''}</td>
                    <td>{g.standard?.processName || g.standard?.processCode || '—'}</td>
                    <td>{g.policyDocument?.title || g.policyDocument?.code || '—'}</td>
                    <td>{t(g.status === 'Not Started' ? 'notStarted' : g.status === 'In Progress' ? 'inProgress' : g.status === 'Non-Compliant' ? 'nonCompliant' : g.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ComplianceReportView;
